/**
 * OpponentPlayerDrawer — Slide-over drawer for opponent player detail
 *
 * Shows NatStat stats, ESPN news, and a chat widget for asking about the player.
 * Opens when clicking an opponent player in the Upcoming Opponent section.
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, MessageSquare, Send, Loader2, Newspaper, BarChart3, User } from 'lucide-react';
import axios from 'axios';

// Extracted outside render to avoid re-creation every render
const StatBox = ({ label, value, highlight }) => (
  <div className="text-center p-3 rounded-lg bg-white/[0.03] border border-[#1A1A1A]">
    <div className={`text-lg font-bold ${highlight ? 'text-[#E8B800]' : 'text-[#F5F5F5]'}`}>
      {value ?? '--'}
    </div>
    <div className="text-[10px] uppercase tracking-wider text-[#777777]">{label}</div>
  </div>
);

const TABS = [
  { id: 'stats', label: 'Stats', icon: BarChart3 },
  { id: 'news', label: 'News', icon: Newspaper },
  { id: 'chat', label: 'Ask AI', icon: MessageSquare },
];

/**
 * @param {Object} props
 * @param {Object} props.player - Player object { player_id, player_name, position, jersey_number, ... }
 * @param {string} props.league - League code (NBA, MBB, etc.)
 * @param {string} props.teamCode - Opponent team code
 * @param {string} props.teamName - Opponent team name
 * @param {Function} props.onClose - Close handler
 */
export default function OpponentPlayerDrawer({ player, league, teamCode, teamName, onClose }) {
  const [stats, setStats] = useState(null);
  const [news, setNews] = useState([]);
  const [statsLoading, setStatsLoading] = useState(true);
  const [newsLoading, setNewsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('stats');

  // Chat state
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [conversationId] = useState(() => `opponent-drawer-${Date.now()}`);
  const chatEndRef = useRef(null);
  const inputRef = useRef(null);

  // Sanitize player name to prevent prompt injection
  const rawName = player?.player_name || player?.name || 'Unknown';
  const playerName = rawName.replace(/[^a-zA-Z0-9\s.\-']/g, '').substring(0, 100);
  const playerId = player?.player_id;

  // Escape key handler
  useEffect(() => {
    const handleEsc = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [onClose]);

  // Fetch player stats from NatStat
  useEffect(() => {
    if (!playerId || !league) return;
    const controller = new AbortController();
    setStatsLoading(true);

    const fetchStats = async () => {
      try {
        const teamParam = teamCode || teamName;
        const url = `/api/natstat/players/${league}?teamId=${encodeURIComponent(teamParam)}&limit=100`;
        const resp = await axios.get(url, { signal: controller.signal });
        const data = resp.data?.data || resp.data;
        const players = data?.players || [];

        const name = playerName.toLowerCase();
        const match = players.find(p => {
          const pName = (p.player_name || p.name || '').toLowerCase();
          return pName === name || p.player_id === playerId;
        });

        setStats(match || null);
      } catch (err) {
        if (err.name === 'CanceledError') return;
        console.warn('[OpponentPlayerDrawer] Stats fetch failed:', err.message);
        setStats(null);
      } finally {
        setStatsLoading(false);
      }
    };

    fetchStats();
    return () => controller.abort();
  }, [playerId, league, teamCode, teamName, playerName]);

  // Fetch ESPN news
  useEffect(() => {
    if (!playerName || playerName === 'Unknown') return;
    const controller = new AbortController();
    setNewsLoading(true);

    const fetchNews = async () => {
      try {
        const resp = await fetch('/api/agent/chat/sync', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            message: `Get the latest ESPN news about ${playerName} from ${teamName || 'the ' + league}. Return just the news headlines and brief summaries.`,
            sessionId: `opponent-news-${Date.now()}`,
            properties: {
              sport: 'basketball',
              playerContext: playerName,
            },
          }),
          signal: controller.signal,
        });

        if (resp.ok) {
          const data = await resp.json();
          const responseText = data.data?.response || data.response || '';
          if (responseText) {
            setNews([{ type: 'ai_summary', content: responseText }]);
          }
        }
      } catch (err) {
        if (err.name === 'AbortError') return;
        console.warn('[OpponentPlayerDrawer] News fetch failed:', err.message);
      } finally {
        setNewsLoading(false);
      }
    };

    fetchNews();
    return () => controller.abort();
  }, [playerName, league, teamName]);

  // Scroll chat to bottom
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  // Send chat message via Code Mode agent (SSE)
  const sendMessage = useCallback(async () => {
    const msg = chatInput.trim();
    if (!msg || chatLoading) return;

    setChatInput('');
    const userMsg = { role: 'user', content: msg };
    setChatMessages(prev => [...prev, userMsg]);
    setChatLoading(true);

    try {
      const resp = await fetch('/api/agent/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: `Regarding ${playerName} (${player?.position || ''}) from ${teamName || league}: ${msg}`,
          sessionId: conversationId,
          properties: {
            sport: 'basketball',
            playerContext: playerName,
          },
        }),
      });

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let botResponse = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value, { stream: true });
        const lines = chunk.split('\n');
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              if (data.type === 'text' && data.content) {
                botResponse += data.content;
              }
            } catch { /* skip non-JSON lines */ }
          }
        }
      }

      setChatMessages(prev => [...prev, { role: 'assistant', content: botResponse || 'No response available.' }]);
    } catch (err) {
      setChatMessages(prev => [...prev, { role: 'assistant', content: `Sorry, I had trouble connecting. Please try again.` }]);
    } finally {
      setChatLoading(false);
    }
  }, [chatInput, chatLoading, playerName, player, teamName, league, conversationId]);

  // Handle Enter key in chat
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40"
        onClick={onClose}
      />

      {/* Drawer */}
      <div className="fixed right-0 top-0 bottom-0 w-full max-w-[480px] bg-[#0A0A0A] border-l border-[#1A1A1A] z-50 flex flex-col animate-slide-in-right">
        {/* Header */}
        <div className="p-4 border-b border-[#1A1A1A] flex items-center gap-4">
          <div className="w-14 h-14 rounded-xl bg-red-500/20 flex items-center justify-center flex-shrink-0">
            {player?.image || player?.photo_url ? (
              <img
                src={player.image || player.photo_url}
                alt={playerName}
                className="w-full h-full rounded-xl object-cover"
                onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
              />
            ) : null}
            <div className={`w-full h-full rounded-xl ${player?.image || player?.photo_url ? 'hidden' : 'flex'} items-center justify-center`}>
              <User size={24} className="text-red-400" />
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-lg font-bold text-[#F5F5F5] truncate">{playerName}</h2>
            <p className="text-sm text-[#999999]">
              {player?.position && <span>{player.position}</span>}
              {player?.jersey_number && <span> • #{player.jersey_number}</span>}
              {teamName && <span> • {teamName}</span>}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-white/[0.04] transition-colors text-[#999999] hover:text-[#F5F5F5]"
          >
            <X size={20} />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-[#1A1A1A]">
          {TABS.map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-1 py-3 flex items-center justify-center gap-2 text-sm font-medium transition-colors ${
                  activeTab === tab.id
                    ? 'text-[#E8B800] border-b-2 border-[#E8B800]'
                    : 'text-[#777777] hover:text-[#999999]'
                }`}
              >
                <Icon size={16} />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {/* Stats Tab */}
          {activeTab === 'stats' && (
            <div className="p-4 space-y-4">
              {statsLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-6 h-6 text-[#E8B800] animate-spin" />
                  <span className="ml-3 text-[#999999]">Loading stats...</span>
                </div>
              ) : stats ? (
                <>
                  <div className="grid grid-cols-3 gap-2">
                    <StatBox label="PPG" value={stats.pts_per_game || stats.ppg} highlight />
                    <StatBox label="RPG" value={stats.reb_per_game || stats.rpg} />
                    <StatBox label="APG" value={stats.ast_per_game || stats.apg} />
                  </div>
                  <div className="grid grid-cols-4 gap-2">
                    <StatBox label="GP" value={stats.games_played || stats.gp} />
                    <StatBox label="MIN" value={stats.min_per_game || stats.mpg} />
                    <StatBox label="FG%" value={stats.fg_pct ? `${(stats.fg_pct * 100).toFixed(1)}` : stats.fg_pct_display} />
                    <StatBox label="3P%" value={stats.three_pct ? `${(stats.three_pct * 100).toFixed(1)}` : stats.three_pct_display} />
                  </div>
                  <div className="grid grid-cols-4 gap-2">
                    <StatBox label="STL" value={stats.stl_per_game || stats.spg} />
                    <StatBox label="BLK" value={stats.blk_per_game || stats.bpg} />
                    <StatBox label="TO" value={stats.tov_per_game || stats.topg} />
                    <StatBox label="FT%" value={stats.ft_pct ? `${(stats.ft_pct * 100).toFixed(1)}` : stats.ft_pct_display} />
                  </div>
                  {(stats.height || stats.weight) && (
                    <div className="p-3 rounded-lg bg-white/[0.03] border border-[#1A1A1A]">
                      <div className="text-xs uppercase tracking-wider text-[#777777] mb-2">Physical</div>
                      <div className="flex gap-6 text-sm text-[#999999]">
                        {stats.height && <span>Height: <span className="text-[#F5F5F5]">{stats.height}</span></span>}
                        {stats.weight && <span>Weight: <span className="text-[#F5F5F5]">{stats.weight} lbs</span></span>}
                        {stats.birthdate && <span>DOB: <span className="text-[#F5F5F5]">{stats.birthdate}</span></span>}
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-12">
                  <BarChart3 size={32} className="mx-auto mb-3 text-white/20" />
                  <p className="text-[#999999]">No stats available for {playerName}</p>
                  <p className="text-xs text-[#555555] mt-1">Try using the Ask AI tab to look up this player</p>
                </div>
              )}
            </div>
          )}

          {/* News Tab */}
          {activeTab === 'news' && (
            <div className="p-4 space-y-3">
              {newsLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-6 h-6 text-[#E8B800] animate-spin" />
                  <span className="ml-3 text-[#999999]">Loading news...</span>
                </div>
              ) : news.length > 0 ? (
                news.map((item, i) => (
                  <div key={i} className="p-4 rounded-lg bg-white/[0.03] border border-[#1A1A1A]">
                    {item.type === 'ai_summary' ? (
                      <div className="text-sm text-[#CCCCCC] whitespace-pre-wrap leading-relaxed">
                        {item.content}
                      </div>
                    ) : (
                      <>
                        <h4 className="font-medium text-[#F5F5F5]">{item.headline}</h4>
                        <p className="text-sm text-[#999999] mt-1">{item.description}</p>
                      </>
                    )}
                  </div>
                ))
              ) : (
                <div className="text-center py-12">
                  <Newspaper size={32} className="mx-auto mb-3 text-white/20" />
                  <p className="text-[#999999]">No recent news for {playerName}</p>
                </div>
              )}
            </div>
          )}

          {/* Chat Tab */}
          {activeTab === 'chat' && (
            <div className="flex flex-col h-full">
              <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {chatMessages.length === 0 && (
                  <div className="text-center py-8">
                    <MessageSquare size={32} className="mx-auto mb-3 text-white/20" />
                    <p className="text-[#999999] text-sm">Ask anything about {playerName}</p>
                    <div className="mt-4 space-y-2">
                      {[
                        `What are ${playerName}'s strengths and weaknesses?`,
                        `How does ${playerName} perform in clutch moments?`,
                        `What's the scouting report on ${playerName}?`,
                      ].map((prompt, i) => (
                        <button
                          key={i}
                          onClick={() => { setChatInput(prompt); inputRef.current?.focus(); }}
                          className="block w-full text-left px-3 py-2 rounded-lg bg-white/[0.03] border border-[#1A1A1A] text-xs text-[#999999] hover:border-[#E8B800]/30 hover:text-[#F5F5F5] transition-colors"
                        >
                          {prompt}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {chatMessages.map((msg, i) => (
                  <div
                    key={i}
                    className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div className={`max-w-[85%] px-4 py-3 rounded-2xl text-sm whitespace-pre-wrap ${
                      msg.role === 'user'
                        ? 'bg-[#E8B800] text-black rounded-br-md'
                        : 'bg-white/[0.06] text-[#CCCCCC] rounded-bl-md border border-[#1A1A1A]'
                    }`}>
                      {msg.content}
                    </div>
                  </div>
                ))}

                {chatLoading && (
                  <div className="flex justify-start">
                    <div className="px-4 py-3 rounded-2xl rounded-bl-md bg-white/[0.06] border border-[#1A1A1A]">
                      <Loader2 className="w-4 h-4 text-[#E8B800] animate-spin" />
                    </div>
                  </div>
                )}
                <div ref={chatEndRef} />
              </div>
            </div>
          )}
        </div>

        {/* Chat Input (always visible when chat tab active) */}
        {activeTab === 'chat' && (
          <div className="p-4 border-t border-[#1A1A1A]">
            <div className="flex items-center gap-2">
              <input
                ref={inputRef}
                type="text"
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={`Ask about ${playerName}...`}
                className="flex-1 px-4 py-2.5 rounded-xl bg-black border border-[#1A1A1A] text-[#F5F5F5] placeholder-white/40 focus:outline-none focus:border-[#E8B800]/50 text-sm"
                disabled={chatLoading}
              />
              <button
                onClick={sendMessage}
                disabled={!chatInput.trim() || chatLoading}
                className="p-2.5 rounded-xl bg-[#E8B800] text-black disabled:opacity-30 disabled:cursor-not-allowed hover:bg-[#D4A800] transition-colors"
              >
                <Send size={18} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Slide-in animation */}
      <style>{`
        @keyframes slide-in-right {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }
        .animate-slide-in-right {
          animation: slide-in-right 0.3s ease-out;
        }
      `}</style>
    </>
  );
}
